import { useEffect, useRef } from 'react';
import { api } from '../lib/api';

const BASE_MS = 30_000;
const MAX_MS = 300_000;

/**
 * Keeps the signed-in user marked "online" via a self-scheduling heartbeat.
 * On success the interval resets to 30 s; on failure it backs off (60 s, 120 s,
 * … up to 5 min) so a flaky network doesn't hammer the API. Mount once, high in
 * the authenticated tree (AppShell).
 */
export const usePresence = (enabled: boolean) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const delay = useRef(BASE_MS);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;

    const beat = async () => {
      try {
        await api.presenceHeartbeat({});
        delay.current = BASE_MS;
      } catch {
        delay.current = Math.min(delay.current * 2, MAX_MS);
      }
      if (!cancelled) {
        timer.current = setTimeout(() => void beat(), delay.current);
      }
    };

    void beat();
    return () => {
      cancelled = true;
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, [enabled]);
};
